import React from 'react';

import { usePermissionsDebug } from '@personnel/presentation/hooks/usePermissionsDebug';

/**
 * Permission Section Component
 *
 * High-level container for a section of permissions.
 * Can contain direct permissions, containers (groups/modules/accordions), or tabs.
 *
 * @param {PermissionSectionProps} props - Component properties
 * @returns {React.ReactElement} Permission section component
 *
 * @example
 * <PermissionSection
 *   name="Permisos del Portal Administrador"
 *   description="Control what the role can view and access"
 * >
 *   <PermissionGroup ... />
 *   <PermissionModule ... />
 * </PermissionSection>
 */

interface PermissionSectionProps {
  /** Section name */
  name: string;
  /** Optional description */
  description?: string;
  /** Child components */
  children: React.ReactNode;
}

const styles = {
  container: 'p-6 border border-border rounded-lg flex flex-col gap-6',
  header: {
    wrapper: '',
    title: 'text-lg font-semibold text-foreground mb-1',
    description: 'text-sm text-muted-foreground whitespace-pre-line',
  },
  content: 'flex flex-col gap-8 [row-rule:1px_solid_oklch(var(--oklch-border))]',
  debug: 'text-xs font-mono p-2 rounded-md bg-purple-50 border border-purple-200 text-purple-700 mt-2 inline-block',
};

export const PermissionSection = ({ name, description, children }: PermissionSectionProps): React.ReactElement => {
  const { isDebugMode } = usePermissionsDebug();

  return (
    <div className={styles.container}>
      {/* Section Header */}
      <div className={styles.header.wrapper}>
        <h2 className={styles.header.title}>{name}</h2>
        {description && <p className={styles.header.description}>{description}</p>}
        {/* Debug info - Only shown in development when enabled via console */}
        {isDebugMode && <span className={styles.debug}>📂 SECTION</span>}
      </div>

      {/* Section Content */}
      <div className={styles.content}>{children}</div>
    </div>
  );
};
